import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import WorkflowTestPanel from "./WorkflowTestPanel";
import WorkflowDiagnosticPanel from "./WorkflowDiagnosticPanel";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import WorkflowsCanvas from "./Canvas/WorkflowsCanvas";
import WorkflowsRightAside from "./RightAside/WorkflowsRightAside";
import WorkflowVersionHistory from "./WorkflowVersionHistory";
import { WorkflowEditorControls } from "./WorkflowsEditorControls";
import { Action, WorkflowNode, NodeId, Workflow } from "./types";
import { ActionMenu } from "./Canvas/CanvasHelpers";
import useApi from "@/hooks/useApi";
import useActivityLog from "@/hooks/useActivityLog";
import useTrackEvent from "@/hooks/useTrackEvent";
import useWorkflowVersions from "@/hooks/useWorkflowVersions";
import LoadingSpinner from "@/components/LoadingSpinner";
import IconArrowBack3 from "@/icons/IconArrowBack3";
import IconEditPencil3 from "@/icons/IconEditPencil3";
import IconAlertTriangle from "@/icons/IconAlertTriangle";
import {
  PrimaryBlueButton,
  SecondaryGrayButton
} from "@velaro/velaro-component-lib";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import { WorkflowContext } from "@/context/WorkflowContext";
import { getNodeOptions, updateNodeOption } from "./WorkflowNodeHelpers";

export type ConnectorData = {
  el: HTMLDivElement;
  parentNodeId: NodeId;
  parentOptionIndex?: number;
  childNodeId?: NodeId;
};

export type DraggingData = {
  event: React.DragEvent<HTMLDivElement>;
  nodeId: NodeId;
  draggedOverConnector?: ConnectorData;
};

export default function WorkflowsEditor() {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const toast = useToastAlert();
  const { track } = useActivityLog();
  const { track: trackTelemetry } = useTrackEvent();
  const { saveVersion } = useWorkflowVersions(id ?? "0");
  const containerRef = useRef<HTMLDivElement>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const changeCountRef = useRef(0);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setTransformRef = useRef<((x: number, y: number, scale: number) => void) | null>(null);
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [loading, setLoading] = useState(true);
  const [scale, setScale] = useState(1);
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [selectedBranchIndex, setSelectedBranchIndex] = useState<number | null>(
    null
  );
  const [selectedConnectorData, setSelectedConnectorData] =
    useState<ConnectorData | null>(null);
  const [draggingData, setDraggingData] = useState<DraggingData | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showProblematicNodesMenu, setShowProblematicNodesMenu] =
    useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [autosavedAt, setAutosavedAt] = useState<string | null>(null);
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [showDiagnosticPanel, setShowDiagnosticPanel] = useState(false);
  const [channelPerspective, setChannelPerspective] = useState<string | null>(null);

  useEffect(() => {
    if (!loading) return;

    const timeout = setTimeout(() => {
      setLoading(false);
      toast.displayToast(Severity.Error, "Could not load workflow — connection timed out. Try refreshing.");
    }, 15_000);

    async function loadWorkflow() {
      try {
        const response = await api.messaging.get(`Workflows/${id}`);
        const json = await response.json();
        if (json.status === 404) {
          toast.displayToast(Severity.Error, "Workflow not found.");
          navigate(-1);
          return;
        }
        setWorkflow(json);
      } catch {
        toast.displayToast(Severity.Error, "Could not load workflow — check your connection.");
      } finally {
        clearTimeout(timeout);
        setLoading(false);
      }
    }

    loadWorkflow();
    return () => clearTimeout(timeout);
  }, [api.messaging, id, loading, navigate, toast]);

  const calcStartPos = useCallback(() => {
    const container = document.getElementsByClassName("workflow-designer")[0];
    const canvas = document.getElementById("canvas");
    const firstNode = document.getElementById("node");

    if (!container || !canvas || !firstNode) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const canvasRect = canvas.getBoundingClientRect();
    const nodeRect = firstNode.getBoundingClientRect();

    // Calculate horizontal center of viewport
    const viewportCenterX = containerRect.width / 2;

    // Account for right aside panel if a node is selected
    const rightAsideOffset = selectedNodeId != null ? 200 : 0;

    // Calculate the node's center position relative to the canvas
    const nodeCenterX = nodeRect.left - canvasRect.left + nodeRect.width / 2;
    const nodeCenterY = nodeRect.top - canvasRect.top + nodeRect.height / 2;

    // Center horizontally, but position near top (100px from top)
    const x = viewportCenterX - nodeCenterX - rightAsideOffset;
    const y = 100 - nodeCenterY;

    return { x, y };
  }, [selectedNodeId]);

  const resetTransform = useCallback(
    (setTransform: (x: number, y: number, scale: number) => void) => {
      const pos = calcStartPos();

      if (pos) {
        setTransform(pos.x, pos.y, scale || 1);
      }
    },
    [calcStartPos, scale]
  );

  const selectedNode = useMemo(() => {
    if (workflow == null || selectedNodeId == null) return undefined;
    return workflow.nodes[selectedNodeId];
  }, [selectedNodeId, workflow]);

  const actionMenuPos = useMemo(() => {
    if (!selectedConnectorData) return null;

    const connectorRect = selectedConnectorData.el.getBoundingClientRect();
    return {
      x: connectorRect.x + 50,
      y: connectorRect.y + 50
    };
  }, [selectedConnectorData]);

  useEffect(() => {
    const onClickAnywhere = () => {
      setSelectedConnectorData(null);
    };

    window.addEventListener("click", onClickAnywhere);

    return () => {
      window.removeEventListener("click", onClickAnywhere);
    };
  }, []);

  // Helper to check if a string value is valid (not empty, undefined, or whitespace)
  function isValidString(value: any): boolean {
    return typeof value === "string" && value.trim().length > 0;
  }

  // Helper to check if a number value is valid (not undefined, NaN, or negative)
  function isValidNumber(value: any, allowZero: boolean = false): boolean {
    if (value === undefined || value === null) return false;
    const num = Number(value);
    if (isNaN(num)) return false;
    return allowZero ? num >= 0 : num > 0;
  }

  // Helper to check if an array has valid items
  function isValidArray(value: any): boolean {
    return Array.isArray(value) && value.length > 0;
  }

  // Helper to validate condition data based on specific condition type
  function validateConditionData(type: string, data: any): boolean {
    // Guard against null/undefined data
    if (!data || typeof data !== "object") return false;
    if (!type || typeof type !== "string") return false;

    // Validate based on specific condition type
    switch (type) {
      case "timeOfDay":
        // Requires both 'from' and 'to' time values
        return isValidString(data.from) && isValidString(data.to);

      case "date":
        // Requires comparator and date value
        return isValidString(data.comparator) && isValidString(data.date);

      case "attribute":
        // Requires attribute name and comparator
        if (!isValidString(data.attribute) || !isValidString(data.comparator)) {
          return false;
        }
        // Value required unless checking existence
        if (data.comparator === "exists" || data.comparator === "notExists") {
          return true;
        }
        return isValidString(data.value);

      case "team":
        // Requires comparator and at least one team selected
        return isValidString(data.comparator) && isValidArray(data.teamIds);

      case "dayOfWeek":
        // Requires at least one day selected
        return isValidArray(data.daysOfWeek);

      case "channel":
        // Requires comparator and channel selection
        return isValidString(data.comparator) && isValidString(data.channel);

      case "domain":
        // Requires comparator and domain value
        return isValidString(data.comparator) && isValidString(data.domain);

      case "url":
        // Requires comparator and url value
        return isValidString(data.comparator) && isValidString(data.url);

      case "timeOnPage":
        // Requires seconds > 0
        return isValidNumber(data.seconds, false);

      case "timePassed":
        // Requires at least one time unit > 0
        return (
          (isValidNumber(data.days, true) ||
            isValidNumber(data.hours, true) ||
            isValidNumber(data.minutes, true)) &&
          ((data.days || 0) > 0 ||
            (data.hours || 0) > 0 ||
            (data.minutes || 0) > 0)
        );

      case "teamAvailability":
        // Requires comparator and status
        return isValidString(data.comparator) && isValidString(data.status);

      case "tag":
        // Requires tagType, hasTag boolean, and valid tagId
        return (
          isValidString(data.tagType) &&
          typeof data.hasTag === "boolean" &&
          isValidNumber(data.tagId, false)
        );

      case "country":
        // Requires comparator and country value
        if (!isValidString(data.comparator)) {
          return false;
        }
        // Value required unless checking existence
        if (data.comparator === "exists" || data.comparator === "notExists") {
          return true;
        }
        return isValidString(data.country);

      default:
        // Unknown condition type - apply generic validation
        // Check if there's at least some data present
        return Object.keys(data).length > 0;
    }
  }

  // Helper to validate individual condition expressions
  function validateConditionExpression(
    expression: any[],
    nodeType: "condition" | "trigger"
  ): { valid: boolean; message: string } {
    // Guard against invalid expression structure
    if (!Array.isArray(expression)) {
      return {
        valid: false,
        message: `Invalid ${
          nodeType === "trigger" ? "trigger " : ""
        }condition structure.`
      };
    }

    for (const andBlock of expression) {
      // Skip empty blocks (defensive programming)
      if (!Array.isArray(andBlock) || andBlock.length === 0) continue;

      for (const condition of andBlock) {
        // Check if condition object exists and has required properties
        if (!condition || typeof condition !== "object") {
          return {
            valid: false,
            message: `${
              nodeType === "trigger" ? "Trigger condition" : "Condition"
            } is incomplete. Please fill all fields.`
          };
        }

        if (!condition.type || !condition.data) {
          return {
            valid: false,
            message: `${
              nodeType === "trigger" ? "Trigger condition" : "Condition"
            } is incomplete. Please fill all fields.`
          };
        }

        // Parse and validate condition data
        let parsedData: any;
        try {
          parsedData = JSON.parse(condition.data);
        } catch (e) {
          return {
            valid: false,
            message: `Invalid ${
              nodeType === "trigger" ? "trigger " : ""
            }condition data.`
          };
        }

        // Check if data is empty object
        if (
          !parsedData ||
          typeof parsedData !== "object" ||
          Object.keys(parsedData).length === 0
        ) {
          return {
            valid: false,
            message: `${
              nodeType === "trigger" ? "Trigger condition" : "Condition"
            } is incomplete. Please fill all fields.`
          };
        }

        // Validate based on specific condition type
        if (!validateConditionData(condition.type, parsedData)) {
          return {
            valid: false,
            message: `${
              nodeType === "trigger" ? "Trigger condition" : "Condition"
            } is incomplete. Please fill all fields.`
          };
        }
      }
    }

    return { valid: true, message: "" };
  }

  // Comparator Validation Helper (checks the payload)
  function validateComparators(workflow: Workflow | null) {
    if (!workflow?.nodes) return { valid: true, message: "" };

    for (const id in workflow.nodes) {
      const node = workflow.nodes[id];

      // Condition node detection
      if (node.type === "action" && node.actionType === "condition") {
        const opts = node.conditionOptions;

        if (!opts || !Array.isArray(opts) || opts.length === 0) {
          return {
            valid: false,
            message: "Condition node is missing condition options."
          };
        }

        for (const o of opts) {
          // Check if the option has an expression
          if (
            !o?.expression ||
            !Array.isArray(o.expression) ||
            o.expression.length === 0
          ) {
            return {
              valid: false,
              message:
                "Condition branch is missing conditions. Please add at least one condition."
            };
          }

          // Validate each condition in the expression
          const result = validateConditionExpression(o.expression, "condition");
          if (!result.valid) return result;
        }
      }

      // Trigger node detection
      if (node.type === "trigger") {
        const triggerExpression = node.triggerExpression;

        // If trigger has conditions, validate them
        if (
          triggerExpression &&
          Array.isArray(triggerExpression) &&
          triggerExpression.length > 0
        ) {
          const result = validateConditionExpression(
            triggerExpression,
            "trigger"
          );
          if (!result.valid) return result;
        }
      }
    }

    return { valid: true, message: "" };
  }

  // Check if a specific node has incomplete conditions (for visual indicators)
  function hasIncompleteConditions(node: WorkflowNode): boolean {
    // Only check condition nodes
    if (node.type === "action" && node.actionType === "condition") {
      const opts = node.conditionOptions;

      // Missing condition options
      if (!opts || !Array.isArray(opts) || opts.length === 0) {
        return true;
      }

      for (const o of opts) {
        // Missing expression
        if (
          !o?.expression ||
          !Array.isArray(o.expression) ||
          o.expression.length === 0
        ) {
          return true;
        }

        // Check each condition in the expression
        const result = validateConditionExpression(o.expression, "condition");
        if (!result.valid) {
          return true;
        }
      }
    }

    // Check trigger node conditions
    if (node.type === "trigger") {
      const triggerExpression = node.triggerExpression;

      if (
        triggerExpression &&
        Array.isArray(triggerExpression) &&
        triggerExpression.length > 0
      ) {
        const result = validateConditionExpression(
          triggerExpression,
          "trigger"
        );
        if (!result.valid) {
          return true;
        }
      }
    }

    return false;
  }

  // Get validation error message for tooltip
  function getValidationErrorMessage(): string | null {
    if (!workflow) return null;
    const validation = validateComparators(workflow);
    if (!validation.valid) {
      return validation.message;
    }
    return null;
  }

  // Get all problematic nodes
  function getProblematicNodes(): WorkflowNode[] {
    if (!workflow) return [];
    return Object.values(workflow.nodes).filter((node) =>
      hasIncompleteConditions(node)
    );
  }

  // Toggle problematic nodes menu
  function toggleProblematicNodesMenu() {
    setShowProblematicNodesMenu(!showProblematicNodesMenu);
  }

  // Restore a workflow version — loads nodes JSON into editor state without saving
  function handleRestoreVersion(nodesJson: string, versionLabel: string) {
    try {
      const restoredNodes = JSON.parse(nodesJson);
      const restored = { ...workflow!, nodes: restoredNodes };
      updateWorkflow(restored);
      setShowVersionHistory(false);
      setIsDirty(true);
      toast.displayToast(
        Severity.Success,
        `Version from ${versionLabel} loaded. Save or Publish to keep it.`
      );
      track({
        category: "Workflows",
        action: "WorkflowVersionRestored",
        entityType: "Workflow",
        entityId: String(workflow?.id),
        entityName: workflow?.name
      });
    } catch {
      toast.displayToast(Severity.Error, "Failed to restore version.");
    }
  }

  // Select a specific problematic node
  function selectProblematicNode(nodeId: number) {
    handleNodeSelect(nodeId);
    setShowProblematicNodesMenu(false);
  }

  async function save(
    workflow: Workflow,
    isPublishing: boolean = false,
    isAutosave: boolean = false
  ) {
    // Autosave runs silently; explicit saves show a toast
    if (!isAutosave) setSaving(true);
    try {
      await api.messaging.put(`Workflows`, workflow);

      if (isAutosave) {
        // Subtle indicator only — no toast
        const now = new Date();
        setAutosavedAt(
          now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        );
      } else {
        setAutosavedAt(null); // clear autosave indicator when user saves manually
        if (isPublishing) {
          toast.displayToast(Severity.Success, "Workflow published successfully");
        } else {
          toast.displayToast(Severity.Success, "Workflow saved as draft");
        }
      }

      setIsDirty(false);
      changeCountRef.current = 0;
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);

      // Snapshot version (best-effort, non-blocking)
      const nodesJson = JSON.stringify(workflow.nodes ?? {});
      const triggerNode = Object.values(workflow.nodes ?? {}).find(
        (n: any) => n.type === "trigger"
      ) as any;
      saveVersion({
        workflowId: workflow.id,
        workflowName: workflow.name ?? "",
        triggerType: triggerNode?.triggerType ?? "",
        nodesJson,
        isPublished: isPublishing,
        isAutosave
      });

      // Activity log — only for explicit user saves
      if (!isAutosave) {
        track({
          category: "Workflows",
          action: isPublishing ? "WorkflowPublished" : "WorkflowSaved",
          entityType: "Workflow",
          entityId: String(workflow.id),
          entityName: workflow.name
        });
        trackTelemetry("feature_click", "Workflows/Saved");
      }
    } catch (e) {
      console.error(e);
      if (!isAutosave) {
        toast.displayToast(
          Severity.Error,
          isPublishing ? "Failed to publish workflow" : "Failed to save workflow"
        );
      }
    }
    if (!isAutosave) setSaving(false);
  }

  async function publish() {
    if (!workflow) return;
    
    const clone = JSON.parse(JSON.stringify(workflow)) as Workflow;
    clone.enabled = true;

    // Validate payload before publishing
    const validation = validateComparators(clone);
    if (!validation.valid) {
      toast.displayToast(
        Severity.Error,
        `Cannot publish workflow: ${validation.message || "Validation failed"}`
      );
      return;
    }

    updateWorkflow(clone);
    await save(clone, true);
  }

  function handleNodeSelect(nodeId: number) {
    setSelectedNodeId(nodeId);
    setSelectedBranchIndex(null);
  }

  function handleBranchSelect(nodeId: number, branchIndex: number) {
    setSelectedNodeId(nodeId);
    setSelectedBranchIndex(branchIndex);
  }

  function handleConnectorSelect(data: ConnectorData) {
    setSelectedConnectorData(data);
    setSelectedNodeId(null);
  }

  function centerOnNode(nodeId: number) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const nodeEl = document.querySelector(`[data-node-id="${nodeId}"]`);
        const container = document.querySelector(".workflow-designer");
        const canvas = document.getElementById("canvas");
        if (!nodeEl || !container || !canvas || !setTransformRef.current) return;

        const containerRect = container.getBoundingClientRect();
        const canvasRect = canvas.getBoundingClientRect();
        const nodeRect = nodeEl.getBoundingClientRect();

        const nodeCenterX = nodeRect.left - canvasRect.left + nodeRect.width / 2;
        const nodeCenterY = nodeRect.top - canvasRect.top + nodeRect.height / 2;

        // Right aside is ~320px, leave room for it
        const rightAsideWidth = 320;
        const availableWidth = containerRect.width - rightAsideWidth;
        const x = availableWidth / 2 - nodeCenterX;
        const y = containerRect.height / 2 - nodeCenterY;

        setTransformRef.current(x, y, scale || 1);
      });
    });
  }

  function injectNewNode(
    actionType: Action,
    connectorData: ConnectorData | null
  ) {
    if (connectorData == null) {
      console.error("No selected connector data");
      return;
    }
    const node = {
      id: getLowestFreeId(),
      type: "action",
      actionType: actionType,
      text: "",
      child: connectorData.childNodeId,
      fallbackChildId: connectorData.childNodeId,
      sendTranscript: true
    } as WorkflowNode;

    injectNode(node, connectorData, workflow!);
    setSelectedConnectorData(null);
    setSelectedNodeId(node.id);
    centerOnNode(node.id);
  }

  function injectNode(
    node: WorkflowNode,
    connectorData: ConnectorData,
    workingModel: Workflow
  ) {
    const clone = JSON.parse(JSON.stringify(workingModel)) as Workflow;
    const parentNode: WorkflowNode = clone.nodes[connectorData.parentNodeId];
    const options = parentNode.options || parentNode.conditionOptions || [];
    const optionIndex = connectorData.parentOptionIndex;
    const hasFailBranch =
      parentNode.actionType === "askQuestion" &&
      parentNode.invalidReplyAction === "failBranch";
    const multipleChoice =
      parentNode.actionType === "askQuestion" &&
      parentNode.questionType === "multipleChoice";
    const hasElseBranch = parentNode.actionType === "condition";

    // If the parent node is an action node and has options, update the option
    //or update the failed branch child if it's a non multiple choice question node
    if (optionIndex !== undefined) {
      if (multipleChoice) {
        if (optionIndex === options.length) {
          parentNode.invalidChild = node.id;
        } else {
          options[optionIndex] = {
            ...options[optionIndex],
            childNodeId: node.id
          };
        }
      } else if (hasFailBranch) {
        if (optionIndex === 0) {
          //0 valid child
          parentNode.child = node.id;
        } else if (optionIndex === 1) {
          //1 invalid child
          parentNode.invalidChild = node.id;
        } else {
          throw new Error(
            "Invalid option index for non multiple choice question node"
          );
        }
      } else if (hasElseBranch) {
        if (optionIndex === options.length) {
          //else branch
          parentNode.fallbackChildId = node.id;
        } else {
          options[optionIndex] = {
            ...options[optionIndex],
            childNodeId: node.id
          };
        }
      }
    } else {
      //otherwise, just update the parent node's child
      parentNode.child = node.id;
    }

    clone.nodes[node.id] = node;
    updateWorkflow(clone);
    return clone;
  }

  const AUTOSAVE_EDIT_THRESHOLD = 5;  // save after this many edits
  const AUTOSAVE_IDLE_MS = 60_000;    // or after 60s of no edits

  function triggerAutosave(wf: Workflow) {
    changeCountRef.current = 0;
    save(wf, false, true); // isAutosave = true
  }

  function updateWorkflow(updatedWorkflow: Workflow) {
    setWorkflow(updatedWorkflow);
    setIsDirty(true);

    // Increment change counter — fire autosave at threshold
    changeCountRef.current += 1;
    if (changeCountRef.current >= AUTOSAVE_EDIT_THRESHOLD) {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      triggerAutosave(updatedWorkflow);
      return;
    }

    // Reset the inactivity timer — fire autosave after idle
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      triggerAutosave(updatedWorkflow);
    }, AUTOSAVE_IDLE_MS);
  }

  function getLowestFreeId() {
    const ids = Object.keys(workflow!.nodes).map(Number);
    let i = 1;
    while (ids.includes(i)) {
      i++;
    }
    return i;
  }

  function onDragStart(nodeId: number, event: React.DragEvent<HTMLDivElement>) {
    event.stopPropagation();
    setDraggingData({ nodeId, event });
  }

  function onDragOverConnector(connectorData: ConnectorData) {
    if (!draggingData) return;
    setDraggingData({ ...draggingData, draggedOverConnector: connectorData });
  }

  function onDragLeaveConnector() {
    if (!draggingData) return;
    setTimeout(() => {
      setDraggingData((prev) => {
        if (!prev) return null;
        return { ...prev, draggedOverConnector: undefined };
      });
    }, 10);
  }

  function onDragEnd(nodeId: number) {
    if (draggingData?.draggedOverConnector) {
      moveNode(nodeId, draggingData.draggedOverConnector);
    }
    setDraggingData(null);
  }

  function moveNode(nodeId: NodeId, connectorData: ConnectorData) {
    const node = { ...workflow!.nodes[nodeId] };
    if (
      connectorData.childNodeId === nodeId ||
      connectorData.parentNodeId === nodeId
    )
      return;

    // Preserve the node's existing structure
    // For nodes with branches (condition, multiple choice), keep their branch structure intact
    // For regular nodes, update the child to point to what's at the drop location
    const hasOwnBranches =
      node.actionType === "condition" ||
      (node.actionType === "askQuestion" &&
        node.questionType === "multipleChoice");

    if (!hasOwnBranches) {
      // Only update child for nodes that use simple child property
      node.child = connectorData.childNodeId;
    }
    // For nodes with branches, they keep their existing branch structure

    const editedWorkflow = removeNode(nodeId);
    if (!editedWorkflow) return;

    injectNode(node, connectorData, editedWorkflow);
  }

  function removeNode(nodeIdToRemove: number) {
    const clone = JSON.parse(JSON.stringify(workflow)) as Workflow;
    const nodeToRemove = clone.nodes[nodeIdToRemove];
    if (!nodeToRemove) return;

    //find the node to remove's child
    //the child id is either the child property or the first option's childNodeId
    const nodeToRemoveOptions = getNodeOptions(nodeToRemove);
    const newChildId = nodeToRemoveOptions?.length
      ? nodeToRemoveOptions[0].childNodeId
      : nodeToRemove.child;

    //find the parent node and update its child
    const nodeIds = Object.keys(clone.nodes).map((x) => parseInt(x) as NodeId);
    nodeIds.forEach((nodeId: NodeId) => {
      const node = clone.nodes[nodeId];
      const nodeOptions = getNodeOptions(node);
      if (!nodeOptions?.length) {
        if (node.child === nodeIdToRemove) {
          node.child = newChildId;
          return;
        }
        return;
      }

      const optionIndex = nodeOptions.findIndex(
        (x) => x.childNodeId === nodeIdToRemove
      );
      if (optionIndex > -1) {
        updateNodeOption(optionIndex, newChildId, node);
      }
    });

    //remove the node
    delete clone.nodes[nodeIdToRemove];
    updateWorkflow(clone);
    return clone;
  }

  function copyNode(nodeIdToCopy: number) {
    const clone = JSON.parse(JSON.stringify(workflow)) as Workflow;
    const nodeToCopy = clone.nodes[nodeIdToCopy];
    if (!nodeToCopy) return;

    const newNodeId = getLowestFreeId();
    const newNode = { ...nodeToCopy, id: newNodeId };

    injectNode(
      newNode,
      { el: actionMenuRef.current!, parentNodeId: nodeToCopy.id },
      clone
    );
  }

  async function handleDetach() {
    if (!workflow) return;
    try {
      await api.messaging.post(`Workflows/detach/${workflow.id}`);
      setWorkflow({ ...workflow, templateId: null, templateName: null });
      toast.displayToast(Severity.Success, "Workflow detached. You can now edit it independently.");
    } catch {
      toast.displayToast(Severity.Error, "Failed to detach from template");
    }
  }

  if (loading) return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-white z-50 gap-4">
      <LoadingSpinner />
      <span className="text-sm text-slate-500">Loading workflow…</span>
    </div>
  );

  if (!workflow) return <div>Workflow not found</div>;

  const isLinked = !!workflow.templateId;

  return (
    <WorkflowContext.Provider value={{ workflow }}>
      <div className="flex flex-col h-full w-full overflow-hidden">
        {workflow.isTemplate && (
          <div
            role="status"
            aria-live="polite"
            className="mb-3 flex flex-none items-center gap-2 px-4 py-2 bg-violet-50 border border-violet-200 rounded-lg text-sm text-violet-800"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
            <span>
              This is a template workflow. Saving changes will automatically update all linked workflows.
            </span>
          </div>
        )}
        {isLinked && (
          <div
            role="status"
            aria-live="polite"
            className="mb-3 flex flex-none items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>
              This workflow follows the template
              {workflow.templateName ? (
                <>
                  {" "}
                  <button
                    type="button"
                    onClick={() => navigate(`/workflows/${workflow.templateId}`)}
                    className="underline font-medium hover:text-amber-900"
                    aria-label={`Go to template: ${workflow.templateName}`}
                  >
                    {workflow.templateName}
                  </button>
                </>
              ) : null}
              . The canvas is read-only.
            </span>
            <button
              type="button"
              onClick={handleDetach}
              className="ml-auto text-xs bg-white border border-amber-300 rounded px-2 py-0.5 hover:bg-amber-100 whitespace-nowrap"
              aria-label="Detach from template"
            >
              Detach from template
            </button>
          </div>
        )}
        <div className="pb-4 mb-4 flex flex-none items-center justify-between gap-4 min-w-0">
          <span className="text-lg font-bold text-gray-800 flex gap-2 items-center min-w-0 flex-1 overflow-hidden">
            <div
              className="cursor-pointer flex-shrink-0"
              onClick={() => navigate("/workflows")}
            >
              <IconArrowBack3 />
            </div>
            <div className="group flex gap-2 items-center min-w-0">
              {editingName ? (
                <input
                  autoFocus
                  onBlur={() => setEditingName(false)}
                  onChange={(e) => {
                    const clone = { ...workflow };
                    clone.name = e.target.value;
                    updateWorkflow(clone);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setEditingName(false);
                  }}
                  type="text"
                  value={workflow.name}
                  className="border-none bg-inherit p-1 min-w-0 w-full"
                />
              ) : (
                <>
                  <span className="truncate">{workflow.name}</span>
                  <div
                    onClick={() => setEditingName(true)}
                    className="cursor-pointer flex-shrink-0"
                  >
                    <IconEditPencil3 className="hover:stroke-blue-500" />
                  </div>
                </>
              )}
            </div>
            <select
              value={workflow.category ?? ""}
              onChange={(e) => updateWorkflow({ ...workflow, category: e.target.value || null })}
              className="ml-3 text-xs border border-slate-200 rounded px-2 py-1 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary-hover"
              title="Category"
            >
              <option value="">No category</option>
              <option value="Lead Capture">Lead Capture</option>
              <option value="Support">Support</option>
              <option value="Sales">Sales</option>
              <option value="Onboarding">Onboarding</option>
              <option value="Custom">Custom</option>
              <option value="Legacy">Legacy</option>
            </select>
          </span>
          <span className="flex gap-2 items-center">
            {getValidationErrorMessage() && (
              <div className="relative group">
                <div
                  className="w-10 h-10 p-2 bg-yellow-50 rounded-lg shadow border border-yellow-300 cursor-pointer hover:bg-yellow-100 transition-colors flex items-center justify-center"
                  onClick={toggleProblematicNodesMenu}
                >
                  <IconAlertTriangle className="text-yellow-600" />
                </div>
                {!showProblematicNodesMenu && (
                  <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 hidden group-hover:block w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-50 pointer-events-none">
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900"></div>
                    <div className="font-semibold mb-1">
                      Cannot publish workflow:
                    </div>
                    {getValidationErrorMessage()}
                  </div>
                )}
                {showProblematicNodesMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowProblematicNodesMenu(false)}
                    />
                    <div className="absolute top-full mt-2 left-0 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
                      <div className="max-h-64 overflow-y-auto">
                        {getProblematicNodes().map((node) => (
                          <div
                            key={node.id}
                            onClick={() => selectProblematicNode(node.id)}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            <div className="font-medium text-sm text-gray-800">
                              {node.name ||
                                `${
                                  node.type === "trigger"
                                    ? "Trigger"
                                    : node.actionType
                                } Node`}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              Click to view and fix conditions
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            {autosavedAt && (
              <span className="text-xs text-slate-400 whitespace-nowrap">
                Autosaved {autosavedAt}
              </span>
            )}
            <SecondaryGrayButton
              size="base"
              label="Test"
              onClick={() => { setShowTestPanel((v) => !v); setShowDiagnosticPanel(false); }}
            />
            <SecondaryGrayButton
              size="base"
              label="Diagnose"
              onClick={() => { setShowDiagnosticPanel((v) => !v); setShowTestPanel(false); }}
            />
            <SecondaryGrayButton
              size="base"
              label="History"
              onClick={() => setShowVersionHistory((v) => !v)}
            />
            {isLinked ? (
              <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-1.5">
                Read-only — detach from template to edit
              </span>
            ) : (
              <>
                <SecondaryGrayButton
                  size="base"
                  label="Save as Draft"
                  onClick={() => save(workflow)}
                  disabled={saving}
                />
                <PrimaryBlueButton
                  size="base"
                  label="Publish"
                  onClick={publish}
                  disabled={saving}
                />
              </>
            )}
          </span>
        </div>

        <div ref={containerRef} className="relative flex flex-1 min-h-0 overflow-hidden">
          {showVersionHistory && (
            <WorkflowVersionHistory
              workflowId={id ?? "0"}
              onRestore={handleRestoreVersion}
              onClose={() => setShowVersionHistory(false)}
            />
          )}
          <div className="flex overflow-hidden cursor-move border border-slate-200 rounded-3xl workflow-designer flex-1 min-h-0 relative">
            <TransformWrapper
              doubleClick={{ disabled: true }}
              minScale={0.1}
              maxScale={2}
              initialScale={1}
              limitToBounds={false}
              panning={{
                disabled: false,
                velocityDisabled: true,
                lockAxisX: false,
                lockAxisY: false
              }}
              wheel={{
                disabled: false,
                step: 0.1,
                wheelDisabled: false,
                touchPadDisabled: false
              }}
              onZoom={(ref) => setScale(ref.state.scale)}
              zoomAnimation={{ disabled: true }}
            >
              {({ setTransform, ...utils }) => {
                setTransformRef.current = setTransform;
                return (
                <>
                  <WorkflowEditorControls
                    zoomIn={(step) => {
                      const newScale = scale + step;
                      if (newScale > 2) return;
                      setScale(newScale);
                      utils.zoomIn(step);
                    }}
                    zoomOut={(step) => {
                      const newScale = scale - step;
                      if (newScale < 0.1) return;
                      setScale(newScale);
                      utils.zoomOut(step);
                    }}
                    scale={scale}
                    resetTransform={() => resetTransform(setTransform)}
                    channelPerspective={channelPerspective}
                    onChannelPerspectiveChange={setChannelPerspective}
                  />
                  <TransformComponent>
                    <WorkflowsCanvas
                      scale={scale}
                      nodes={workflow!.nodes}
                      draggingData={draggingData}
                      selectedNodeId={selectedNodeId}
                      selectedBranchIndex={selectedBranchIndex}
                      selectedConnector={selectedConnectorData?.el}
                      channelPerspective={channelPerspective}
                      onLoad={() => resetTransform(setTransform)}
                      onSelectNode={handleNodeSelect}
                      onSelectBranch={handleBranchSelect}
                      onSelectConnector={handleConnectorSelect}
                      onDeleteNode={removeNode}
                      onCopyNode={copyNode}
                      onDragStart={onDragStart}
                      onDragOverConnector={onDragOverConnector}
                      onDragLeaveConnector={onDragLeaveConnector}
                      onDragEnd={onDragEnd}
                    />
                  </TransformComponent>
                </>
              );
              }}
            </TransformWrapper>

            {selectedNode && (
              <WorkflowsRightAside
                node={selectedNode}
                selectedBranchIndex={selectedBranchIndex}
                onUpdate={(updates) => {
                  const newNode = { ...selectedNode, ...updates };
                  const cloneWorkflow = JSON.parse(
                    JSON.stringify(workflow)
                  ) as Workflow;
                  cloneWorkflow.nodes[selectedNode.id] = newNode;
                  if (newNode.type === "trigger") {
                    cloneWorkflow.triggerType = newNode.trigger;
                  }
                  updateWorkflow(cloneWorkflow);
                }}
                onClose={() => setSelectedNodeId(null)}
              />
            )}
          </div>
        </div>

        {actionMenuPos && (
          <div
            ref={actionMenuRef}
            onClick={(e) => e.stopPropagation()}
            className="fixed"
            style={{ top: actionMenuPos.y, left: actionMenuPos.x }}
          >
            <ActionMenu
              onSelect={(nodeId) =>
                injectNewNode(nodeId, selectedConnectorData)
              }
            />
          </div>
        )}
      </div>

      {showTestPanel && workflow && (
        <WorkflowTestPanel
          workflowId={id ?? "0"}
          workflowName={workflow.name ?? "Workflow"}
          onClose={() => setShowTestPanel(false)}
        />
      )}

      {showDiagnosticPanel && workflow && (
        <WorkflowDiagnosticPanel
          workflowId={id ?? "0"}
          workflowName={workflow.name ?? "Workflow"}
          triggerType={workflow.triggerType ?? ""}
          onClose={() => setShowDiagnosticPanel(false)}
        />
      )}
    </WorkflowContext.Provider>
  );
}
